import React, { useState, useEffect } from 'react';
import { Button, Form, Table, Modal, Alert, Spinner, Row, Col } from 'react-bootstrap';
import { AiOutlinePlus, AiOutlineEdit, AiOutlineDelete } from 'react-icons/ai';
import axios from '../utils/api';
import RichTextEditor from './RichTextEditor';
import './AdminDashboard.css';

function WorkExperienceEditor() {
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingExperience, setEditingExperience] = useState(null);
  const [formData, setFormData] = useState({
    job_title: '',
    company: '',
    location: '',
    start_date: '',
    end_date: '',
    description: '',
    technologies: '',
    employment_type: 'Full-time',
    company_website: ''
  });

  useEffect(() => {
    fetchExperiences();
  }, []);

  const fetchExperiences = async () => {
    try {
      const response = await axios.get('/content/experience-education?type=work-experience');
      // Sort by: NULL end_date first (current positions), then end_date DESC, then start_date DESC
      const sorted = (response.data.data || []).sort((a, b) => {
        // Current positions (no end_date) come first
        const aIsCurrent = !a.end_date;
        const bIsCurrent = !b.end_date;
        
        if (aIsCurrent && !bIsCurrent) return -1;
        if (!aIsCurrent && bIsCurrent) return 1;
        
        // If both are current or both are past, sort by end_date DESC
        if (!aIsCurrent && !bIsCurrent) {
          const endDateA = new Date(a.end_date);
          const endDateB = new Date(b.end_date);
          const endDateDiff = endDateB - endDateA; // DESC
          if (endDateDiff !== 0) return endDateDiff;
        }
        
        // If same end date (or both current), sort by start_date DESC
        const startDateA = new Date(a.start_date);
        const startDateB = new Date(b.start_date);
        return startDateB - startDateA; // DESC
      });
      setExperiences(sorted);
    } catch (err) {
      setError('Failed to load work experience');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (experience = null) => {
    if (experience) {
      setEditingExperience(experience);
      setFormData({
        job_title: experience.job_title || '',
        company: experience.company || '',
        location: experience.location || '',
        start_date: experience.start_date ? experience.start_date.split('T')[0] : '',
        end_date: experience.end_date ? experience.end_date.split('T')[0] : '',
        description: experience.description || '',
        technologies: experience.technologies || '',
        employment_type: experience.employment_type || 'Full-time',
        company_website: experience.company_website || ''
      });
    } else {
      setEditingExperience(null);
      setFormData({
        job_title: '',
        company: '',
        location: '',
        start_date: '',
        end_date: '',
        description: '',
        technologies: '',
        employment_type: 'Full-time',
        company_website: ''
      });
    }
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingExperience(null);
    setFormData({
      job_title: '',
      company: '',
      location: '',
      start_date: '',
      end_date: '',
      description: '',
      technologies: ''
    });
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.job_title || !formData.company || !formData.start_date) {
      setError('Job title, company, and start date are required');
      return;
    }

    try {
      if (editingExperience) {
        await axios.put('/content/experience-education?type=work-experience', {
          id: editingExperience.id,
          ...formData
        });
        setSuccess('Work experience updated successfully');
      } else {
        await axios.post('/content/experience-education?type=work-experience', formData);
        setSuccess('Work experience added successfully');
      }
      
      handleCloseModal();
      fetchExperiences();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save work experience');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this work experience?')) {
      return;
    }

    try {
      await axios.delete('/content/experience-education?type=work-experience', { data: { id } });
      setSuccess('Work experience deleted successfully');
      fetchExperiences();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete work experience');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Present';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="loading-spinner">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading work experience...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="editor-card">
      {error && <Alert variant="danger" className="alert-admin" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" className="alert-admin" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      <div className="editor-header">
        <h3>💼 Manage Work Experience</h3>
        <Button className="btn-primary-admin" onClick={() => handleOpenModal()}>
          <AiOutlinePlus style={{ marginRight: '0.5rem' }} />
          Add New Experience
        </Button>
      </div>

      {experiences.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📋</div>
          <h4>No work experience yet</h4>
          <p>Start building your professional history!</p>
          <Button className="btn-primary-admin mt-3" onClick={() => handleOpenModal()}>
            <AiOutlinePlus style={{ marginRight: '0.5rem' }} />
            Add Your First Experience
          </Button>
        </div>
      ) : (
        <Table className="admin-table" responsive>
          <thead>
            <tr>
              <th>Job Title</th>
              <th>Company</th>
              <th>Location</th>
              <th>Duration</th>
              <th>Status / Type</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {experiences.map((exp) => (
              <tr key={exp.id}>
                <td><strong>{exp.job_title}</strong></td>
                <td>
                  {exp.company_website ? (
                    <a href={exp.company_website} target="_blank" rel="noopener noreferrer" style={{ color: '#667eea', textDecoration: 'none' }}>
                      {exp.company} 🔗
                    </a>
                  ) : (
                    exp.company
                  )}
                </td>
                <td>{exp.location || '-'}</td>
                <td>
                  {formatDate(exp.start_date)} - {exp.end_date ? formatDate(exp.end_date) : 'Present'}
                </td>
                <td>
                  <div>
                    <span style={{ 
                      display: 'inline-block',
                      background: !exp.end_date 
                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                        : '#e0e0e0',
                      color: !exp.end_date ? 'white' : '#666',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '20px',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      marginBottom: '0.25rem'
                    }}>
                      {!exp.end_date ? 'Current' : 'Past'}
                    </span>
                    <br />
                    <span style={{ 
                      display: 'inline-block',
                      background: '#f0f0f0',
                      color: '#667eea',
                      padding: '0.2rem 0.6rem',
                      borderRadius: '15px',
                      fontSize: '0.8rem',
                      fontWeight: '500',
                      marginTop: '0.25rem'
                    }}>
                      {exp.employment_type || 'Full-time'}
                    </span>
                  </div>
                </td>
                <td>
                  <Button 
                    className="btn-outline-primary-admin me-2" 
                    size="sm"
                    onClick={() => handleOpenModal(exp)}
                  >
                    <AiOutlineEdit style={{ marginRight: '0.25rem' }} />
                    Edit
                  </Button>
                  <Button 
                    className="btn-danger-admin" 
                    size="sm"
                    onClick={() => handleDelete(exp.id)}
                  >
                    <AiOutlineDelete style={{ marginRight: '0.25rem' }} />
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal show={showModal} onHide={handleCloseModal} size="lg" className="admin-modal">
        <Modal.Header closeButton>
          <Modal.Title>{editingExperience ? '✏️ Edit Work Experience' : '➕ Add Work Experience'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit} className="admin-form">
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Job Title *</Form.Label>
              <Form.Control
                type="text"
                value={formData.job_title}
                onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                placeholder="e.g., Junior IoT Developer"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Company *</Form.Label>
              <Form.Control
                type="text"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                placeholder="e.g., Tech Company Inc."
                required
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Employment Type</Form.Label>
                  <Form.Select
                    value={formData.employment_type}
                    onChange={(e) => setFormData({ ...formData, employment_type: e.target.value })}
                  >
                    <option value="Full-time">Full-time</option>
                    <option value="Part-time">Part-time</option>
                    <option value="Internship">Internship</option>
                    <option value="CDI">CDI (Permanent Contract)</option>
                    <option value="CDD">CDD (Fixed-term Contract)</option>
                    <option value="Freelance">Freelance</option>
                    <option value="Contract">Contract</option>
                    <option value="Temporary">Temporary</option>
                    <option value="Other">Other</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Location</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="e.g., Brussels, Belgium"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Company Website</Form.Label>
              <Form.Control
                type="url"
                value={formData.company_website}
                onChange={(e) => setFormData({ ...formData, company_website: e.target.value })}
                placeholder="https://www.company.com"
              />
              <Form.Text className="text-muted">
                Enter the full URL including https://
              </Form.Text>
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Start Date *</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>End Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    min={formData.start_date}
                  />
                  <Form.Text className="text-muted">
                    Leave empty if this is your current position
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <RichTextEditor
                value={formData.description}
                onChange={(value) => setFormData({ ...formData, description: value })}
                placeholder="Describe your role, responsibilities, and achievements..."
                rows={4}
              />
              <Form.Text className="text-muted">Use the toolbar above to format your description (Bold, Italic, Lists, etc.).</Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Technologies</Form.Label>
              <Form.Control
                type="text"
                value={formData.technologies}
                onChange={(e) => setFormData({ ...formData, technologies: e.target.value })}
                placeholder="e.g., React, Node.js, Python, IoT"
              />
              <Form.Text className="text-muted">
                Enter technologies separated by commas (e.g., React, Node.js, Python)
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal} style={{ borderRadius: '10px' }}>
              Cancel
            </Button>
            <Button className="btn-primary-admin" type="submit">
              {editingExperience ? '💾 Update Experience' : '✨ Add Experience'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}

export default WorkExperienceEditor;

